import { Input, Button, SimpleAlert } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../../../../../components/toast/Toast';
import { FYI_URL } from '../../../../../../config';
import { useIsActiveSubscription } from '../../../../../../hooks/use-is-active-subscription';
import { useCreateFYIConfig } from '../../../../../../queries/use-create-fyi-config';
import { useUpdateFYIConfig } from '../../../../../../queries/use-update-fyi-config';

type FirmFYICredentialsFormProps = {
    mode?: 'create' | 'update';
    onCancel?: () => void;
};

function FirmFYICredentialsForm({ mode = 'create', onCancel }: FirmFYICredentialsFormProps): JSX.Element {
    const createFYIConfig = useCreateFYIConfig();
    const updateFYIConfig = useUpdateFYIConfig();
    const activeSubscription = useIsActiveSubscription();

    const [id, setId] = useState({ value: '', error: '' });
    const [secret, setSecret] = useState({ value: '', error: '' });
    const [error, setError] = useState<string>();

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        setError(undefined);
        let hasErrors = false;
        if (!id.value) {
            setId({ value: id.value, error: 'An access key id is required.' });
            hasErrors = true;
        }
        if (!secret.value) {
            setSecret({ value: secret.value, error: 'An access key secret is required.' });
            hasErrors = true;
        }
        if (hasErrors) {
            return;
        }

        if (mode === 'create') {
            createFYIConfig.mutate(
                { accessKeyId: id.value, accessKeySecret: secret.value },
                {
                    onSuccess: () => {
                        toastNotification('success', 'Saved FYI credentials!');
                        if (onCancel) {
                            onCancel();
                        }
                    },
                    onError: (e) => {
                        setError(e.response?.data.message);
                    },
                }
            );
        } else {
            updateFYIConfig.mutate(
                { accessKeyId: id.value, accessKeySecret: secret.value },
                {
                    onSuccess: () => {
                        toastNotification('success', 'Replaced FYI credentials!');
                        if (onCancel) {
                            onCancel();
                        }
                    },
                    onError: (e) => {
                        setError(e.response?.data.message);
                    },
                }
            );
        }
    }

    return (
        <div>
            {mode === 'create' && (
                <p className="text-sm text-gray-600 my-2">
                    Please enter your FYI keys for Cimplico Workpapers. You can find them&nbsp;
                    <a href={`${FYI_URL}/integrations/cimplico-workpapers`} target="_blank" rel="noreferrer" className="text-indigo-600">
                        here
                    </a>
                    .
                </p>
            )}
            {error && <SimpleAlert type="error" title={error} />}
            <form className="flex flex-col gap-4 mt-2" onSubmit={handleSubmit}>
                <Input
                    type="text"
                    placeholder="Access Key Secret"
                    label="Access key secret"
                    autoFocus
                    isError={Boolean(secret.error)}
                    errorMessage={secret.error}
                    onChange={(e): void => {
                        setSecret({ value: e.target.value, error: '' });
                    }}
                    disabled={!activeSubscription}
                />
                <Input
                    type="text"
                    placeholder="Access Key ID"
                    label="Access key id"
                    isError={Boolean(id.error)}
                    errorMessage={id.error}
                    onChange={(e): void => {
                        setId({ value: e.target.value, error: '' });
                    }}
                    disabled={!activeSubscription}
                />
                <span className="self-end flex gap-2">
                    {onCancel && <Button type="hollow" onClick={onCancel} label="Cancel" />}
                    <Button
                        type="primary"
                        label={createFYIConfig.isLoading ? 'Saving...' : 'Save'}
                        size="sm"
                        isSubmit
                        isLoading={createFYIConfig.isLoading}
                        disabled={!activeSubscription || createFYIConfig.isLoading}
                    />
                </span>
            </form>
        </div>
    );
}
export default FirmFYICredentialsForm;
